import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { planAPI, ticketTemplateAPI } from '../services/api';
import { ArrowLeft, LayoutTemplate, Save, RefreshCw, Image as ImageIcon, Type, Hash, QrCode, DollarSign, ArrowUp, ArrowDown, ArrowLeft as ArrowLeftIcon, ArrowRight } from 'lucide-react';

const elementDefinitions = [
  { key: 'logo', label: 'Logo', icon: ImageIcon },
  { key: 'business_name', label: 'Nombre del Negocio', icon: Type },
  { key: 'plan_name', label: 'Nombre del Plan', icon: Type },
  { key: 'user_pass_pin', label: 'Usuario / Password / PIN', icon: Hash },
  { key: 'qr', label: 'Código QR', icon: QrCode },
  { key: 'price', label: 'Precio', icon: DollarSign },
  { key: 'extra_text', label: 'Texto Adicional', icon: Type },
];

const defaultElementConfig = {
  visible: true,
  x: 10,
  y: 10,
  size: 40,
  text: '',
  logoUrl: '', // Para almacenar la URL del logo
  fontFamily: 'Arial', // Tipo de fuente
  fontWeight: 'normal', // normal, bold
  textShadow: false, // Con o sin sombra
  lineHeight: 1.5, // Espaciado entre líneas
};

const TicketDesignerPage = ({ isEmbedded = false, editTemplateId = null }) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const [templates, setTemplates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const [name, setName] = useState('');
  const [activeLayout, setActiveLayout] = useState('carta'); // 'carta' o 'ticket'

  const [layoutCarta, setLayoutCarta] = useState({});
  const [layoutTicket, setLayoutTicket] = useState({});

  const [stylesCarta, setStylesCarta] = useState({
    background_color: '#FFFFFF',
    border_color: '#000000',
    primary_text_color: '#000000',
    secondary_text_color: '#4B5563',
  });
  const [stylesTicket, setStylesTicket] = useState({
    background_color: '#FFFFFF',
    border_color: '#000000',
    primary_text_color: '#000000',
    secondary_text_color: '#4B5563',
  });

  const [assignedPlanIds, setAssignedPlanIds] = useState([]);
  const [selectedElementKey, setSelectedElementKey] = useState('user_pass_pin');
  const [elementDropdownOpen, setElementDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, [editTemplateId]);

  const createInitialLayout = () => {
    const layout = {};
    elementDefinitions.forEach((el) => {
      // Valores por defecto según el tipo de layout
      let defaultConfig = { ...defaultElementConfig };
      
      if (el.key === 'qr') {
        defaultConfig = {
          visible: true,
          x: 35,
          y: 15,
          size: 179,
        };
      } else if (el.key === 'user_pass_pin') {
        defaultConfig = {
          visible: true,
          x: 55,
          y: 35,
          size: 147,
        };
      } else {
        defaultConfig = {
          ...defaultElementConfig,
          visible: false,
        };
      }
      
      layout[el.key] = defaultConfig;
    });
    return layout;
  };

  const createInitialLayoutTicket = () => {
    const layout = {};
    elementDefinitions.forEach((el) => {
      let defaultConfig = { ...defaultElementConfig };
      
      if (el.key === 'qr') {
        defaultConfig = {
          visible: true,
          x: 15,
          y: 10,
          size: 133,
        };
      } else if (el.key === 'user_pass_pin') {
        defaultConfig = {
          visible: true,
          x: 15,
          y: 65,
          size: 87,
        };
      } else {
        defaultConfig = {
          ...defaultElementConfig,
          visible: false,
        };
      }
      
      layout[el.key] = defaultConfig;
    });
    return layout;
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [templateRes, plansRes] = await Promise.all([
        ticketTemplateAPI.list(),
        planAPI.getAll(), // se cargarán todos los planes del usuario actual
      ]);
      setTemplates(templateRes.data || []);
      setPlans(plansRes.data || []);

      // Si hay editTemplateId, cargar esa plantilla
      if (editTemplateId && templateRes.data) {
        const templateToEdit = templateRes.data.find(t => t.id === editTemplateId);
        if (templateToEdit) {
          loadTemplate(templateToEdit);
        } else {
          resetDesigner();
        }
      } else if (templateRes.data && templateRes.data.length > 0 && !isEmbedded) {
        // Si no está embebido y hay plantillas, seleccionar la primera
        loadTemplate(templateRes.data[0]);
      } else {
        resetDesigner();
      }
    } catch (err) {
      console.error('Error loading ticket templates or plans:', err);
      setError('Error cargando plantillas o planes');
      resetDesigner();
    } finally {
      setLoading(false);
    }
  };

  const resetDesigner = () => {
    setSelectedTemplateId(null);
    setName('Nuevo Diseño');
    setActiveLayout('carta');
    setLayoutCarta(createInitialLayout());
    setLayoutTicket(createInitialLayoutTicket());
    setStylesCarta({
      background_color: '#FFFFFF',
      border_color: '#000000',
      primary_text_color: '#000000',
      secondary_text_color: '#4B5563',
    });
    setStylesTicket({
      background_color: '#FFFFFF',
      border_color: '#000000',
      primary_text_color: '#000000',
      secondary_text_color: '#4B5563',
    });
    setAssignedPlanIds([]);
    setSelectedElementKey('user_pass_pin');
    setError('');
  };

  const loadTemplate = (template) => {
    setSelectedTemplateId(template.id);
    setName(template.name);
    setActiveLayout('carta');

    const layoutCartaData = template.layout_carta || {};
    const layoutTicketData = template.layout_ticket || {};

    const carta = {};
    const ticket = {};
    elementDefinitions.forEach((el) => {
      carta[el.key] = {
        ...defaultElementConfig,
        ...(layoutCartaData[el.key] || {}),
      };
      ticket[el.key] = {
        ...defaultElementConfig,
        ...(layoutTicketData[el.key] || {}),
      };
    });
    setLayoutCarta(carta);
    setLayoutTicket(ticket);

    setStylesCarta({
      background_color: template.styles_carta?.background_color || '#FFFFFF',
      border_color: template.styles_carta?.border_color || '#000000',
      primary_text_color: template.styles_carta?.primary_text_color || '#000000',
      secondary_text_color: template.styles_carta?.secondary_text_color || '#4B5563',
    });
    setStylesTicket({
      background_color: template.styles_ticket?.background_color || '#FFFFFF',
      border_color: template.styles_ticket?.border_color || '#000000',
      primary_text_color: template.styles_ticket?.primary_text_color || '#000000',
      secondary_text_color: template.styles_ticket?.secondary_text_color || '#4B5563',
    });

    setAssignedPlanIds(template.assigned_plan_ids || []);
    setSelectedElementKey('user_pass_pin');
    setError('');
  };

  const getActiveLayoutConfig = () => {
    return activeLayout === 'carta' ? layoutCarta : layoutTicket;
  };

  const setActiveLayoutConfig = (updater) => {
    if (activeLayout === 'carta') {
      setLayoutCarta((prev) => updater(prev));
    } else {
      setLayoutTicket((prev) => updater(prev));
    }
  };

  const getActiveStyles = () => {
    return activeLayout === 'carta' ? stylesCarta : stylesTicket;
  };

  const setActiveStyles = (partial) => {
    if (activeLayout === 'carta') {
      setStylesCarta((prev) => ({ ...prev, ...partial }));
    } else {
      setStylesTicket((prev) => ({ ...prev, ...partial }));
    }
  };

  const handleElementChange = (key, partial) => {
    setActiveLayoutConfig((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...partial,
      },
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setError('Solo se permiten archivos PNG, JPG o SVG');
      return;
    }

    // Validar tamaño (máx 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('El logo no debe superar 2MB');
      return;
    }

    // Convertir a base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      handleElementChange('logo', { logoUrl: base64String });
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('El diseño debe tener un nombre');
      return;
    }
    // Abrir modal para seleccionar planes
    setShowPlanModal(true);
  };

  const handleFinalizeAndSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        name,
        layout_carta: layoutCarta,
        layout_ticket: layoutTicket,
        styles_carta: stylesCarta,
        styles_ticket: stylesTicket,
        assigned_plan_ids: assignedPlanIds,
      };

      if (selectedTemplateId) {
        const res = await ticketTemplateAPI.update(selectedTemplateId, payload);
        const updated = res.data;
        setTemplates((prev) => prev.map((tpl) => (tpl.id === updated.id ? updated : tpl)));
        loadTemplate(updated);
      } else {
        const res = await ticketTemplateAPI.create(payload);
        const created = res.data;
        setTemplates((prev) => [...prev, created]);
        loadTemplate(created);
      }
      setShowPlanModal(false);
    } catch (err) {
      console.error('Error saving template:', err);
      setError(err.response?.data?.detail || 'Error al guardar diseño');
    } finally {
      setSaving(false);
    }
  };

  const activeLayoutConfig = getActiveLayoutConfig();
  const selectedElement = activeLayoutConfig[selectedElementKey] || defaultElementConfig;

  const togglePlanAssignment = (planId) => {
    setAssignedPlanIds((prev) =>
      prev.includes(planId) ? prev.filter((id) => id !== planId) : [...prev, planId]
    );
  };

  const renderPreview = () => {
    const isCarta = activeLayout === 'carta';
    const activeStyles = getActiveStyles();
    const activeElements = getActiveLayoutConfig();

    // QR real generado desde API pública
    const StaticQR = ({ size, isSelected }) => (
      <div
        className={`bg-white border-2 rounded flex items-center justify-center overflow-hidden ${
          isSelected ? 'ring-4 ring-red-500 ring-offset-2' : 'border-gray-300'
        }`}
        style={{ width: `${size}px`, height: `${size}px`, padding: '8px' }}
      >
        <img
          src={`https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=https://www.etweb.mx&margin=0`}
          alt="QR Code"
          className="w-full h-full"
          style={{ imageRendering: 'crisp-edges' }}
        />
      </div>
    );

    if (isCarta) {
      // Vista Carta: Layout horizontal con marco interno
      return (
        <div
          className="relative w-full h-96 rounded-2xl overflow-hidden"
          style={{ backgroundColor: activeStyles.background_color }}
        >
          {/* Marco punteado interno */}
          <div className="absolute inset-0 flex items-center justify-center p-8">
            <div 
              className="relative border-2 border-dashed p-8 flex items-center justify-center gap-8"
              style={{ 
                borderColor: '#60a5fa',
                width: '80%',
                height: '70%',
                minHeight: '300px'
              }}
            >
              {/* Textos Usuario/Password (izquierda) */}
              {activeElements.user_pass_pin?.visible && (
                <div
                  className={`flex flex-col cursor-pointer p-3 rounded-lg ${
                    selectedElementKey === 'user_pass_pin' ? 'border-3 border-red-500 bg-red-50' : ''
                  }`}
                  onClick={() => setSelectedElementKey('user_pass_pin')}
                  style={{
                    position: 'absolute',
                    left: `${activeElements.user_pass_pin.x || 10}%`,
                    top: `${activeElements.user_pass_pin.y || 40}%`,
                    color: activeStyles.primary_text_color,
                    fontSize: `${Math.min((activeElements.user_pass_pin.size || 60) / 5, 16)}px`,
                    fontFamily: activeElements.user_pass_pin.fontFamily || 'Arial',
                    fontWeight: activeElements.user_pass_pin.fontWeight || 'normal',
                    textShadow: activeElements.user_pass_pin.textShadow ? '2px 2px 4px rgba(0,0,0,0.3)' : 'none',
                    lineHeight: activeElements.user_pass_pin.lineHeight || 1.5,
                    gap: `${((activeElements.user_pass_pin.lineHeight || 1.5) - 1) * 10}px`,
                  }}
                >
                  <div className="font-medium">
                    <span className="font-semibold">Usuario:</span> <span className="text-orange-600">15M001</span>
                  </div>
                  <div className="font-medium">
                    <span className="font-semibold">Password:</span> <span className="text-gray-700">abc123</span>
                  </div>
                </div>
              )}

              {/* Código QR (derecha) */}
              {activeElements.qr?.visible && (
                <div
                  className="absolute cursor-pointer"
                  onClick={() => setSelectedElementKey('qr')}
                  style={{
                    right: `${100 - (activeElements.qr.x || 60) - 15}%`,
                    top: `${activeElements.qr.y || 20}%`,
                  }}
                >
                  <StaticQR 
                    size={activeElements.qr.size || 100} 
                    isSelected={selectedElementKey === 'qr'}
                  />
                </div>
              )}

              {/* Otros elementos */}
              {elementDefinitions.filter(el => !['qr', 'user_pass_pin'].includes(el.key)).map((el) => {
                const cfg = activeElements[el.key];
                if (!cfg?.visible) return null;
                const isSelected = selectedElementKey === el.key;

                return (
                  <div
                    key={el.key}
                    className={`absolute rounded cursor-pointer flex items-center justify-center ${
                      isSelected ? 'border-2 border-red-500 bg-red-50' : 'border border-gray-300 bg-white'
                    }`}
                    style={{
                      left: `${cfg.x || 0}%`,
                      top: `${cfg.y || 0}%`,
                      width: `${cfg.size || 60}px`,
                      minHeight: `${Math.min(cfg.size || 60, 40)}px`,
                      color: activeStyles.primary_text_color,
                      fontSize: `${Math.min((cfg.size || 60) / 4, 16)}px`,
                      padding: '8px',
                      fontFamily: cfg.fontFamily || 'Arial',
                      fontWeight: cfg.fontWeight || 'normal',
                      textShadow: cfg.textShadow ? '2px 2px 4px rgba(0,0,0,0.3)' : 'none',
                      lineHeight: cfg.lineHeight || 1.5,
                    }}
                    onClick={() => setSelectedElementKey(el.key)}
                  >
                    {el.key === 'logo' && cfg.logoUrl ? (
                      <img
                        src={cfg.logoUrl}
                        alt="Logo"
                        className="object-contain"
                        style={{ width: '100%', height: '100%' }}
                      />
                    ) : el.key === 'logo' ? (
                      <span className="font-semibold">LOGO</span>
                    ) : el.key === 'business_name' ? (
                      <span className="font-semibold">NEGOCIO</span>
                    ) : el.key === 'plan_name' ? (
                      <span className="font-semibold">PLAN</span>
                    ) : el.key === 'extra_text' ? (
                      <span className="font-medium">{cfg.text || 'TEXTO'}</span>
                    ) : el.key === 'price' ? (
                      <span className="font-semibold">PRECIO</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    } else {
      // Vista Térmica: Layout con posicionamiento manual
      return (
        <div
          className="relative w-80 h-96 rounded-2xl overflow-hidden mx-auto"
          style={{ backgroundColor: activeStyles.background_color }}
        >
          {/* Marco punteado interno */}
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div 
              className="relative border-2 border-dashed p-6"
              style={{ 
                borderColor: '#60a5fa',
                width: '75%',
                height: '85%'
              }}
            >
              {/* Código QR con posicionamiento manual */}
              {activeElements.qr?.visible && (
                <div
                  className="absolute cursor-pointer"
                  onClick={() => setSelectedElementKey('qr')}
                  style={{
                    left: `${activeElements.qr.x || 10}%`,
                    top: `${activeElements.qr.y || 10}%`,
                  }}
                >
                  <StaticQR 
                    size={activeElements.qr.size || 120} 
                    isSelected={selectedElementKey === 'qr'}
                  />
                </div>
              )}

              {/* Textos Usuario/Password con posicionamiento manual */}
              {activeElements.user_pass_pin?.visible && (
                <div
                  className={`absolute flex flex-col cursor-pointer p-3 rounded-lg ${
                    selectedElementKey === 'user_pass_pin' ? 'border-3 border-red-500 bg-red-50' : ''
                  }`}
                  onClick={() => setSelectedElementKey('user_pass_pin')}
                  style={{
                    left: `${activeElements.user_pass_pin.x || 10}%`,
                    top: `${activeElements.user_pass_pin.y || 40}%`,
                    color: activeStyles.primary_text_color,
                    fontSize: `${Math.min((activeElements.user_pass_pin.size || 60) / 5, 16)}px`,
                    fontFamily: activeElements.user_pass_pin.fontFamily || 'Arial',
                    fontWeight: activeElements.user_pass_pin.fontWeight || 'normal',
                    textShadow: activeElements.user_pass_pin.textShadow ? '2px 2px 4px rgba(0,0,0,0.3)' : 'none',
                    lineHeight: activeElements.user_pass_pin.lineHeight || 1.5,
                    gap: `${((activeElements.user_pass_pin.lineHeight || 1.5) - 1) * 10}px`,
                  }}
                >
                  <div className="font-medium">
                    <span className="font-semibold">Usuario:</span> <span className="text-orange-600">15M001</span>
                  </div>
                  <div className="font-medium">
                    <span className="font-semibold">Pass:</span> <span className="text-gray-700">abc123</span>
                  </div>
                </div>
              )}

              {/* Otros elementos con posicionamiento manual */}
              {elementDefinitions.filter(el => !['qr', 'user_pass_pin'].includes(el.key)).map((el) => {
                const cfg = activeElements[el.key];
                if (!cfg?.visible) return null;
                const isSelected = selectedElementKey === el.key;

                return (
                  <div
                    key={el.key}
                    className={`absolute rounded cursor-pointer flex items-center justify-center ${
                      isSelected ? 'border-2 border-red-500 bg-red-50' : 'border border-gray-300 bg-white'
                    }`}
                    style={{
                      left: `${cfg.x || 0}%`,
                      top: `${cfg.y || 0}%`,
                      width: `${cfg.size || 60}px`,
                      minHeight: `${Math.min(cfg.size || 60, 40)}px`,
                      color: activeStyles.primary_text_color,
                      fontSize: `${Math.min((cfg.size || 60) / 4, 16)}px`,
                      padding: '8px',
                      fontFamily: cfg.fontFamily || 'Arial',
                      fontWeight: cfg.fontWeight || 'normal',
                      textShadow: cfg.textShadow ? '2px 2px 4px rgba(0,0,0,0.3)' : 'none',
                      lineHeight: cfg.lineHeight || 1.5,
                    }}
                    onClick={() => setSelectedElementKey(el.key)}
                  >
                    {el.key === 'logo' && cfg.logoUrl ? (
                      <img
                        src={cfg.logoUrl}
                        alt="Logo"
                        className="object-contain"
                        style={{ width: '100%', height: '100%' }}
                      />
                    ) : el.key === 'logo' ? (
                      <span className="font-semibold">LOGO</span>
                    ) : el.key === 'business_name' ? (
                      <span className="font-semibold">NEGOCIO</span>
                    ) : el.key === 'plan_name' ? (
                      <span className="font-semibold">PLAN</span>
                    ) : el.key === 'extra_text' ? (
                      <span className="font-medium">{cfg.text || 'TEXTO'}</span>
                    ) : el.key === 'price' ? (
                      <span className="font-semibold">PRECIO</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Header (solo si no está embebido) */}
      {!isEmbedded && (
        <header className={`${isDark ? 'bg-gray-800 border-b border-gray-700' : 'bg-white'} shadow-sm`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className={`p-2 rounded-lg ${
                  isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Editor de Ficha
                </h1>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Diseña cómo se verán tus vouchers para impresión carta o térmica
                </p>
              </div>
            </div>
          </div>
        </header>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Columna izquierda: Editor */}
          <div className="space-y-6">
            {/* 1. Nombre del diseño */}
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Nombre del Diseño *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej: Fichas 15 MIN Azules"
                className={`w-full px-4 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300'
                }`}
              />
            </div>

            {/* 2. Editar Elemento */}
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
              <div className="flex items-center gap-2 mb-3">
                <LayoutTemplate className="w-5 h-5 text-blue-600" />
                <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Editar Elemento
                </h2>
              </div>

              {/* Dropdown del elemento activo */}
              <div className="relative mb-4">
                <button
                  type="button"
                  onClick={() => setElementDropdownOpen(!elementDropdownOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 ${
                    isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {React.createElement(elementDefinitions.find(el => el.key === selectedElementKey)?.icon || Type, { className: "w-4 h-4" })}
                    {elementDefinitions.find(el => el.key === selectedElementKey)?.label || 'Selecciona objeto'}
                  </span>
                  <span className="text-gray-400">▼</span>
                </button>

                {/* Lista de elementos */}
                {elementDropdownOpen && (
                  <div className={`absolute z-10 w-full mt-2 rounded-lg border-2 border-blue-500 overflow-hidden ${
                    isDark ? 'bg-gray-700' : 'bg-white'
                  } shadow-lg`}>
                    <div className="p-2 space-y-1">
                      {elementDefinitions.map((el) => {
                        const Icon = el.icon;
                        const cfg = activeLayoutConfig[el.key] || defaultElementConfig;
                        const isSelected = selectedElementKey === el.key;
                        return (
                          <div
                            key={el.key}
                            onClick={() => {
                              setSelectedElementKey(el.key);
                              setElementDropdownOpen(false);
                            }}
                            className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer ${
                              isSelected
                                ? 'bg-blue-600 text-white'
                                : isDark
                                  ? 'hover:bg-gray-600 text-gray-200'
                                  : 'hover:bg-gray-100 text-gray-800'
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={cfg.visible}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleElementChange(el.key, { visible: e.target.checked });
                                }}
                                className="w-5 h-5"
                              />
                              <Icon className="w-4 h-4" />
                              {el.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div className={`px-3 py-2 text-xs border-t ${
                      isDark ? 'bg-gray-800 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'
                    }`}>
                      ☑ = Visible en la ficha | ☐ = Oculto | Clic en el nombre para editar
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Columna derecha: Vista Previa */}
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
            {/* 3. Vista Previa */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl">👁</span>
                <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Vista Previa
                </h2>
              </div>
              {/* Tabs Carta / Térmica */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setActiveLayout('carta')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium border-2 ${
                    activeLayout === 'carta'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : isDark
                        ? 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'
                        : 'bg-gray-200 text-gray-700 border-gray-300 hover:bg-gray-300'
                  }`}
                >
                  <LayoutTemplate className="w-4 h-4" />
                  Carta
                </button>
                <button
                  type="button"
                  onClick={() => setActiveLayout('ticket')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium border-2 ${
                    activeLayout === 'ticket'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : isDark
                        ? 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'
                        : 'bg-gray-200 text-gray-700 border-gray-300 hover:bg-gray-300'
                  }`}
                >
                  <LayoutTemplate className="w-4 h-4" />
                  Térmica
                </button>
              </div>
            </div>

            {/* Preview */}
            {renderPreview()}

            {/* Ayuda */}
            <div className={`mt-4 p-3 rounded-lg border ${
              isDark ? 'bg-blue-900 border-blue-700 text-blue-200' : 'bg-blue-50 border-blue-300 text-blue-700'
            }`}>
              <p className="text-xs flex items-start gap-2">
                <span className="text-lg">💡</span>
                <span>
                  Haz clic en un elemento de la lista para editarlo. El elemento seleccionado se marca con borde rojo en la vista previa.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* 4. Propiedades del elemento (debajo, ancho completo) */}
        <div className={`mt-6 ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <span>🎨</span> Propiedades: 
            {React.createElement(elementDefinitions.find(el => el.key === selectedElementKey)?.icon || Type, { className: "w-4 h-4 ml-1" })}
            <span className="ml-1">{elementDefinitions.find(el => el.key === selectedElementKey)?.label}</span>
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Columna 1: Posición */}
            <div>
              <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Posición (X: {selectedElement.x}, Y: {selectedElement.y})
              </label>
              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
                <div className="flex flex-col items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleElementChange(selectedElementKey, { y: Math.max(0, (selectedElement.y || 0) - 5) })}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-lg font-bold"
                  >
                    ↑
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleElementChange(selectedElementKey, { x: Math.max(0, (selectedElement.x || 0) - 5) })}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-lg font-bold"
                    >
                      ←
                    </button>
                    <div className="w-12 h-12 rounded-full bg-gray-800"></div>
                    <button
                      type="button"
                      onClick={() => handleElementChange(selectedElementKey, { x: Math.min(100, (selectedElement.x || 0) + 5) })}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-lg font-bold"
                    >
                      →
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleElementChange(selectedElementKey, { y: Math.min(100, (selectedElement.y || 0) + 5) })}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-lg font-bold"
                  >
                    ↓
                  </button>
                </div>
                <p className={`text-center text-xs mt-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Cada clic mueve 5 pixeles
                </p>
              </div>
            </div>

            {/* Columna 2: Tamaño */}
            <div>
              <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Tamaño: {selectedElement.size || 40}px
              </label>
              <input
                type="range"
                min="10"
                max={selectedElementKey === 'qr' ? '200' : '150'}
                value={selectedElement.size || 40}
                onChange={(e) => handleElementChange(selectedElementKey, { size: Number(e.target.value) })}
                className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
              />

              {/* Subir logo (solo para Logo) */}
              {selectedElementKey === 'logo' && (
                <div className="mt-4">
                  <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Imagen del Logo
                  </label>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                    onChange={handleLogoUpload}
                    className={`w-full px-3 py-2 text-xs border rounded-lg ${
                      isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    PNG, JPG o SVG (máx. 2MB)
                  </p>
                  {selectedElement.logoUrl && (
                    <div className="mt-2 flex items-center gap-2">
                      <img
                        src={selectedElement.logoUrl}
                        alt="Logo preview"
                        className="w-12 h-12 object-contain border rounded"
                      />
                      <button
                        type="button"
                        onClick={() => handleElementChange('logo', { logoUrl: '' })}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Eliminar
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Texto adicional */}
              {selectedElementKey === 'extra_text' && (
                <div className="mt-4">
                  <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Texto
                  </label>
                  <input
                    type="text"
                    value={selectedElement.text || ''}
                    onChange={(e) => handleElementChange(selectedElementKey, { text: e.target.value })}
                    className={`w-full px-3 py-2 text-sm border rounded-lg ${
                      isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
              )}
            </div>

            {/* Columna 3: Estilos de texto y colores */}
            <div>
              {/* Estilos de texto (para elementos de texto) */}
              {!['logo', 'qr'].includes(selectedElementKey) && (
                <div className="space-y-3 mb-4 pb-4 border-b">
                  <h4 className={`text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Estilos de Texto
                  </h4>
                  
                  {/* Tipo de fuente */}
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      Tipo de Letra
                    </label>
                    <select
                      value={selectedElement.fontFamily || 'Arial'}
                      onChange={(e) => handleElementChange(selectedElementKey, { fontFamily: e.target.value })}
                      className={`w-full px-3 py-2 text-sm border rounded-lg ${
                        isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    >
                      <option value="Arial">Arial</option>
                      <option value="Helvetica">Helvetica</option>
                      <option value="Times New Roman">Times New Roman</option>
                      <option value="Georgia">Georgia</option>
                      <option value="Courier New">Courier New</option>
                      <option value="Verdana">Verdana</option>
                      <option value="Impact">Impact</option>
                      <option value="Comic Sans MS">Comic Sans MS</option>
                    </select>
                  </div>

                  {/* Negrita */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedElement.fontWeight === 'bold'}
                      onChange={(e) => handleElementChange(selectedElementKey, { fontWeight: e.target.checked ? 'bold' : 'normal' })}
                      className="w-4 h-4"
                    />
                    <label className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      Negrita
                    </label>
                  </div>

                  {/* Sombra */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedElement.textShadow || false}
                      onChange={(e) => handleElementChange(selectedElementKey, { textShadow: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      Sombra de Texto
                    </label>
                  </div>

                  {/* Espaciado entre líneas */}
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      Espaciado entre líneas: {selectedElement.lineHeight || 1.5}
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="0.1"
                      value={selectedElement.lineHeight || 1.5}
                      onChange={(e) => handleElementChange(selectedElementKey, { lineHeight: Number(e.target.value) })}
                      className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>
              )}

              {/* Estilos de colores */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Fondo
                  </label>
                  <input
                    type="color"
                    value={getActiveStyles().background_color}
                    onChange={(e) => setActiveStyles({ background_color: e.target.value })}
                    className="w-full h-10 rounded cursor-pointer"
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Borde
                  </label>
                  <input
                    type="color"
                    value={getActiveStyles().border_color}
                    onChange={(e) => setActiveStyles({ border_color: e.target.value })}
                    className="w-full h-10 rounded cursor-pointer"
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Texto principal
                  </label>
                  <input
                    type="color"
                    value={getActiveStyles().primary_text_color}
                    onChange={(e) => setActiveStyles({ primary_text_color: e.target.value })}
                    className="w-full h-10 rounded cursor-pointer"
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Texto secundario
                  </label>
                  <input
                    type="color"
                    value={getActiveStyles().secondary_text_color}
                    onChange={(e) => setActiveStyles({ secondary_text_color: e.target.value })}
                    className="w-full h-10 rounded cursor-pointer"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 5. Botones de acción (debajo de todo) */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowPreviewModal(true)}
            className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-lg border-2 ${
              isDark
                ? 'border-blue-600 text-blue-400 hover:bg-blue-900/30'
                : 'border-blue-600 text-blue-600 hover:bg-blue-50'
            }`}
          >
            👁 Previsualizar Ambos Formatos
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center justify-center gap-2 px-8 py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium text-lg disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Guardando...' : 'Guardar Diseño'}
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-red-100 border border-red-400 text-red-700 text-sm">
            {error}
          </div>
        )}
      </div>


      {/* Modal de previsualización de ambos formatos */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Previsualización: {name || 'Nuevo Diseño'}
              </h2>
              <button
                type="button"
                onClick={() => setShowPreviewModal(false)}
                className={`px-4 py-2 rounded-lg font-medium ${
                  isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                ✕ Cerrar
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Vista Carta */}
              <div>
                <h3 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  📄 Formato Carta (A4)
                </h3>
                <div className="relative w-full h-96 rounded-xl overflow-hidden border-4 border-gray-300" style={{ backgroundColor: stylesCarta.background_color }}>
                  <div className="absolute inset-0 flex items-center justify-center p-8">
                    <div className="relative border-2 border-dashed p-8" style={{ borderColor: '#60a5fa', width: '80%', height: '70%', minHeight: '300px' }}>
                      {layoutCarta.qr?.visible && (
                        <div className="absolute" style={{ right: `${100 - (layoutCarta.qr.x || 60) - 15}%`, top: `${layoutCarta.qr.y || 20}%` }}>
                          <div className="bg-white border-2 border-gray-300 rounded flex items-center justify-center overflow-hidden" style={{ width: `${layoutCarta.qr.size || 100}px`, height: `${layoutCarta.qr.size || 100}px`, padding: '8px' }}>
                            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=${layoutCarta.qr.size || 100}x${layoutCarta.qr.size || 100}&data=https://www.etweb.mx&margin=0`} alt="QR" className="w-full h-full" style={{ imageRendering: 'crisp-edges' }} />
                          </div>
                        </div>
                      )}
                      {layoutCarta.user_pass_pin?.visible && (
                        <div className="absolute flex flex-col p-3 rounded-lg" style={{ left: `${layoutCarta.user_pass_pin.x || 10}%`, top: `${layoutCarta.user_pass_pin.y || 40}%`, color: stylesCarta.primary_text_color, fontSize: `${Math.min((layoutCarta.user_pass_pin.size || 60) / 5, 16)}px`, fontFamily: layoutCarta.user_pass_pin.fontFamily || 'Arial', fontWeight: layoutCarta.user_pass_pin.fontWeight || 'normal', textShadow: layoutCarta.user_pass_pin.textShadow ? '2px 2px 4px rgba(0,0,0,0.3)' : 'none' }}>
                          <div className="font-medium"><span className="font-semibold">Usuario:</span> <span className="text-orange-600">15M001</span></div>
                          <div className="font-medium"><span className="font-semibold">Password:</span> <span className="text-gray-700">abc123</span></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Vista Térmica */}
              <div>
                <h3 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  🎫 Formato Térmica (Ticket)
                </h3>
                <div className="relative w-80 h-96 rounded-xl overflow-hidden mx-auto border-4 border-gray-300" style={{ backgroundColor: stylesTicket.background_color }}>
                  <div className="absolute inset-0 flex items-center justify-center p-6">
                    <div className="relative border-2 border-dashed p-6" style={{ borderColor: '#60a5fa', width: '75%', height: '85%' }}>
                      {layoutTicket.qr?.visible && (
                        <div className="absolute" style={{ left: `${layoutTicket.qr.x || 10}%`, top: `${layoutTicket.qr.y || 10}%` }}>
                          <div className="bg-white border-2 border-gray-300 rounded flex items-center justify-center overflow-hidden" style={{ width: `${layoutTicket.qr.size || 120}px`, height: `${layoutTicket.qr.size || 120}px`, padding: '8px' }}>
                            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=${layoutTicket.qr.size || 120}x${layoutTicket.qr.size || 120}&data=https://www.etweb.mx&margin=0`} alt="QR" className="w-full h-full" style={{ imageRendering: 'crisp-edges' }} />
                          </div>
                        </div>
                      )}
                      {layoutTicket.user_pass_pin?.visible && (
                        <div className="absolute flex flex-col p-3 rounded-lg" style={{ left: `${layoutTicket.user_pass_pin.x || 10}%`, top: `${layoutTicket.user_pass_pin.y || 40}%`, color: stylesTicket.primary_text_color, fontSize: `${Math.min((layoutTicket.user_pass_pin.size || 60) / 5, 16)}px`, fontFamily: layoutTicket.user_pass_pin.fontFamily || 'Arial', fontWeight: layoutTicket.user_pass_pin.fontWeight || 'normal', textShadow: layoutTicket.user_pass_pin.textShadow ? '2px 2px 4px rgba(0,0,0,0.3)' : 'none' }}>
                          <div className="font-medium"><span className="font-semibold">Usuario:</span> <span className="text-orange-600">15M001</span></div>
                          <div className="font-medium"><span className="font-semibold">Pass:</span> <span className="text-gray-700">abc123</span></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para seleccionar planes */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl p-6 w-96 max-h-[80vh] overflow-y-auto`}>
            <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Asignar Diseño a Planes
            </h2>
            
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Selecciona los planes a los que deseas aplicar este diseño:
            </p>

            {plans.length === 0 ? (
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                No hay planes disponibles.
              </p>
            ) : (
              <div className="space-y-2 mb-6 max-h-60 overflow-y-auto">
                {plans.map((plan) => (
                  <label 
                    key={plan.id} 
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border ${
                      assignedPlanIds.includes(plan.id)
                        ? 'border-blue-500 bg-blue-50'
                        : isDark
                          ? 'border-gray-600 hover:bg-gray-700'
                          : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={assignedPlanIds.includes(plan.id)}
                      onChange={() => togglePlanAssignment(plan.id)}
                      className="w-5 h-5"
                    />
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {plan.name}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-100 border border-red-400 text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowPlanModal(false)}
                className={`flex-1 px-4 py-2 rounded-lg border ${
                  isDark
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleFinalizeAndSave}
                disabled={saving}
                className="flex-1 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50"
              >
                {saving ? 'Guardando...' : 'Finalizar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de previsualización de ambos formatos */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Previsualización: {name || 'Nuevo Diseño'}
              </h2>
              <button
                type="button"
                onClick={() => setShowPreviewModal(false)}
                className={`px-4 py-2 rounded-lg ${
                  isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                ✕ Cerrar
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Vista Carta */}
              <div>
                <h3 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  📄 Formato Carta (A4)
                </h3>
                <div className="relative w-full h-96 rounded-xl overflow-hidden" style={{ backgroundColor: stylesCarta.background_color }}>
                  <div className="absolute inset-0 flex items-center justify-center p-8">
                    <div className="relative border-2 border-dashed p-8" style={{ borderColor: '#60a5fa', width: '80%', height: '70%', minHeight: '300px' }}>
                      {/* QR Carta */}
                      {layoutCarta.qr?.visible && (
                        <div className="absolute" style={{ right: `${100 - (layoutCarta.qr.x || 60) - 15}%`, top: `${layoutCarta.qr.y || 20}%` }}>
                          <div className="bg-white border-2 border-gray-300 rounded flex items-center justify-center overflow-hidden" style={{ width: `${layoutCarta.qr.size || 100}px`, height: `${layoutCarta.qr.size || 100}px`, padding: '8px' }}>
                            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=${layoutCarta.qr.size || 100}x${layoutCarta.qr.size || 100}&data=https://www.etweb.mx&margin=0`} alt="QR" className="w-full h-full" style={{ imageRendering: 'crisp-edges' }} />
                          </div>
                        </div>
                      )}
                      {/* Usuario/Pass Carta */}
                      {layoutCarta.user_pass_pin?.visible && (
                        <div className="absolute flex flex-col p-3 rounded-lg" style={{ left: `${layoutCarta.user_pass_pin.x || 10}%`, top: `${layoutCarta.user_pass_pin.y || 40}%`, color: stylesCarta.primary_text_color, fontSize: `${Math.min((layoutCarta.user_pass_pin.size || 60) / 5, 16)}px`, fontFamily: layoutCarta.user_pass_pin.fontFamily || 'Arial', fontWeight: layoutCarta.user_pass_pin.fontWeight || 'normal', textShadow: layoutCarta.user_pass_pin.textShadow ? '2px 2px 4px rgba(0,0,0,0.3)' : 'none' }}>
                          <div className="font-medium"><span className="font-semibold">Usuario:</span> <span className="text-orange-600">15M001</span></div>
                          <div className="font-medium"><span className="font-semibold">Password:</span> <span className="text-gray-700">abc123</span></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Vista Térmica */}
              <div>
                <h3 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  🎫 Formato Térmica (Ticket)
                </h3>
                <div className="relative w-80 h-96 rounded-xl overflow-hidden mx-auto" style={{ backgroundColor: stylesTicket.background_color }}>
                  <div className="absolute inset-0 flex items-center justify-center p-6">
                    <div className="relative border-2 border-dashed p-6" style={{ borderColor: '#60a5fa', width: '75%', height: '85%' }}>
                      {/* QR Ticket */}
                      {layoutTicket.qr?.visible && (
                        <div className="absolute" style={{ left: `${layoutTicket.qr.x || 10}%`, top: `${layoutTicket.qr.y || 10}%` }}>
                          <div className="bg-white border-2 border-gray-300 rounded flex items-center justify-center overflow-hidden" style={{ width: `${layoutTicket.qr.size || 120}px`, height: `${layoutTicket.qr.size || 120}px`, padding: '8px' }}>
                            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=${layoutTicket.qr.size || 120}x${layoutTicket.qr.size || 120}&data=https://www.etweb.mx&margin=0`} alt="QR" className="w-full h-full" style={{ imageRendering: 'crisp-edges' }} />
                          </div>
                        </div>
                      )}
                      {/* Usuario/Pass Ticket */}
                      {layoutTicket.user_pass_pin?.visible && (
                        <div className="absolute flex flex-col p-3 rounded-lg" style={{ left: `${layoutTicket.user_pass_pin.x || 10}%`, top: `${layoutTicket.user_pass_pin.y || 40}%`, color: stylesTicket.primary_text_color, fontSize: `${Math.min((layoutTicket.user_pass_pin.size || 60) / 5, 16)}px`, fontFamily: layoutTicket.user_pass_pin.fontFamily || 'Arial', fontWeight: layoutTicket.user_pass_pin.fontWeight || 'normal', textShadow: layoutTicket.user_pass_pin.textShadow ? '2px 2px 4px rgba(0,0,0,0.3)' : 'none' }}>
                          <div className="font-medium"><span className="font-semibold">Usuario:</span> <span className="text-orange-600">15M001</span></div>
                          <div className="font-medium"><span className="font-semibold">Pass:</span> <span className="text-gray-700">abc123</span></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl p-6 w-96 max-h-[80vh] overflow-y-auto`}>
            <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Asignar Diseño a Planes
            </h2>
            
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Selecciona los planes a los que deseas aplicar este diseño:
            </p>

            {plans.length === 0 ? (
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                No hay planes disponibles.
              </p>
            ) : (
              <div className="space-y-2 mb-6 max-h-60 overflow-y-auto">
                {plans.map((plan) => (
                  <label 
                    key={plan.id} 
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border ${
                      assignedPlanIds.includes(plan.id)
                        ? 'border-blue-500 bg-blue-50'
                        : isDark
                          ? 'border-gray-600 hover:bg-gray-700'
                          : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={assignedPlanIds.includes(plan.id)}
                      onChange={() => togglePlanAssignment(plan.id)}
                      className="w-5 h-5"
                    />
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {plan.name}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-100 border border-red-400 text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowPlanModal(false)}
                className={`flex-1 px-4 py-2 rounded-lg border ${
                  isDark
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleFinalizeAndSave}
                disabled={saving}
                className="flex-1 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50"
              >
                {saving ? 'Guardando...' : 'Finalizar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDesignerPage;
